import {ApolloClient, ApolloLink, InMemoryCache} from '@apollo/client/core'
import {onError} from '@apollo/client/link/error'
import {DocumentTransform} from '@apollo/client/utilities'
import {captureException, captureMessage, withScope} from '@sentry/react'
import {get as getCookies} from 'es-cookie'
import {makeVar} from '@apollo/client'
import {Kind, visit} from 'graphql'
import * as Sentry from '@sentry/react'

import {
  CI_RELEASE_TAG_NAME,
  APP_NAME,
  GQL_SCHEMA_HASH,
  MEMBER_ID,
  TAB_UUID,
} from '@sdox/utils/dom'
import {uuidv4} from '@sdox/utils'

import generatedGraphql from './generated/possibleTypes'
import {createApolloLink} from './createApolloLink'
import clientSchema from './documents/client.schema'

import type {StrictTypedTypePolicies} from './generated/clientHelpers'

let serverSchemaHash = GQL_SCHEMA_HASH.toString()

const headerMiddleware = new ApolloLink((operation, forward) => {

  const operationName = operation.operationName ?? undefined
  if (operationName){
    Sentry.addBreadcrumb({category: 'apollo.operationName', message: operationName, level: 'info', data: operation.variables})
  }

  // this will strip the undefineds
  const headers = JSON.parse(
    JSON.stringify({
      'X-CSRFToken': getCookies('csrftoken') ?? undefined,
      'X-Apollo': 1,
      'X-Apollo-Operation': operationName,
      'X-Request-Uuid': uuidv4(),
      'X-Tab-Uuid': TAB_UUID,
      'X-Member-Id': MEMBER_ID,
      'X-Release': CI_RELEASE_TAG_NAME,
      'X-App-Name': APP_NAME,
      'X-Search': window.location.search,
      'X-Gql-Schema-Hash': serverSchemaHash ?? undefined,
      'X-Cache-Access-Uuid': getCookies('cache_access_uuid') ?? undefined,
    }),
  )
  operation.setContext({
    headers,
  })
  return forward(operation).map((data) => {
    const context = operation.getContext()
    const responseSchemaHash =
      context.response?.headers.get('x-gql-schema-hash') ?? ''
    if (responseSchemaHash.length === 16) {
      serverSchemaHash = responseSchemaHash
    }
    return data
  })
})

const errorLink = onError(({graphQLErrors, networkError, operation}) => {
  withScope((scope) => {
    scope.setTag('operation.name', operation.operationName)
    scope.setExtra('operation.variables', operation.variables)
    scope.setTag('operation.schemaHash', serverSchemaHash)
    const context = operation.getContext()
    graphQLErrors?.forEach(({message: originalMessage, extensions}, idx) => {
      // Mantainance
      if (extensions?.code === 503) {
        window.location.reload()
        return
      }
      // schema missmatch
      if (extensions?.code === 409) {
        const newSchemaHash = context.response?.headers.get('x-gql-schema-hash')
        const currentUrl = new URL(window.location.href)
        currentUrl.searchParams.set('cache', newSchemaHash) // cache buster
        window.location.href = currentUrl.toString()
        return
      }
      const message = (extensions?.message as string) ?? originalMessage
      const path = ((extensions?.path as string[]) ?? []).join(' > ')
      scope.setExtra(`operation.extensions.${idx}`, extensions)
      scope.addBreadcrumb({
        category: 'query:query',
        message: path,
        level: 'debug',
      })
      captureMessage(message, 'error')
    })
    if (networkError) {
      captureException(networkError)
    }
  })

  // Do not forward or it will have a retry effect
  //https://www.apollographql.com/docs/react/data/error-handling/#on-graphql-errors
})

export const isThemeEditorVar = makeVar(false)
export const isXcEditorVar = makeVar(false)

const clientFields = {
  isThemeEditor() {
    return isThemeEditorVar()
  },
  isXcEditor() {
    return isXcEditorVar()
  },
  isCufeApp() {
    return APP_NAME === 'cufe'
  },
  isEufeApp() {
    return APP_NAME === 'xc-eufe'
  },
}

export const getGraphQlFragmentVariables = () => ({
  isThemeEditor: isThemeEditorVar(),
  isXcEditor: isXcEditorVar(),
  isCufeApp: APP_NAME === 'cufe',
  isEufeApp: APP_NAME === 'xc-eufe',
})

const typePolicies: StrictTypedTypePolicies = {
  Query: {
    fields: clientFields,
  },
  Mutation: {
    fields: clientFields,
  },
  Subscription: {
    fields: clientFields,
  },
  XCollectionAutomotiveType: {
    fields: {
      editContent: {
        merge: false,
      },
    },
  },
  DocumentType: {
    fields:{
      pages: {
        merge: false,
      },
    },
  },
  XCollectionSeType: {
    fields: {
      mobRecord(_, {args, toReference}) {
        if (!args) throw new Error('Programming error')
        return toReference({
          __typename: 'MobRecordType',
          id: args.mobRecordId,
        })
      },
      mobRecords: {
        keyArgs: [
          'documentIds',
          'textSearch',
          'filterModellComplex',
          'filterStock',
          'filterComplex',
          'asAdmin',
        ],
        merge(existing, incoming) {
          const items = [...(existing?.items ?? [])]

          incoming?.items.forEach((data: {__ref: string}) => {
            const found = items.find(
              ({__ref}: {__ref: string}) => __ref === data.__ref,
            )
            if (!found) {
              items.push(data)
            }
          })

          return {
            total: incoming.total,
            items,
          }
        },
      },
    },
  },
  NestedContentButtonType: {
    fields: {
      panel: {
        merge: false,
      },
    },
  },
  PanelType: {
    fields: {
      blocks: {
        merge: false,
      },
    },
  },
}

const createClientExportField = (value: string) => ({
  kind: Kind.FIELD,
  name: {kind: Kind.NAME, value},
  directives: [
    {kind: Kind.DIRECTIVE, name: {kind: Kind.NAME, value: 'client'}},
    {
      kind: Kind.DIRECTIVE,
      name: {kind: Kind.NAME, value: 'export'},
      arguments: [
        {
          kind: Kind.ARGUMENT,
          name: {kind: Kind.NAME, value: 'as'},
          value: {kind: Kind.STRING, value},
        },
      ],
    },
  ],
})

const documentTransform = new DocumentTransform((document) => {
  const transformedDocument = visit(document, {
    OperationDefinition(node) {
      return {
        ...node,
        selectionSet: {
          ...node.selectionSet,
          selections: [
            ...['isThemeEditor', 'isXcEditor', 'isCufeApp', 'isEufeApp'].map(
              createClientExportField,
            ),
            ...node.selectionSet.selections,
          ],
        },
      }
    },
  })
  return transformedDocument
})

export const client = new ApolloClient({
  link: ApolloLink.from([errorLink, headerMiddleware, createApolloLink()]),
  cache: new InMemoryCache({
    possibleTypes: generatedGraphql.possibleTypes,
    typePolicies,
  }),
  documentTransform,
  typeDefs: clientSchema,
  defaultOptions: {
    watchQuery: {
      nextFetchPolicy(lastFetchPolicy) {
        if (
          lastFetchPolicy === 'cache-and-network' ||
          lastFetchPolicy === 'network-only'
        ) {
          return 'cache-first'
        }
        return lastFetchPolicy
      },
    },
  },
})
